import { Alert, Anchor, Badge, Button, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntityLabel } from '../../components/EntityField';
import { RecordEditModal } from '../../components/RecordEditModal';
import { EmptyNote, RecordScreen } from '../../components/RecordScreen';
import { enumLabel } from '../../lib/enums';
import { formatDate, formatDateTime, formatMoney, formatRateBp, toIsoDate } from '../../lib/format';
import { dialablePhone, formatPhone } from '../../lib/phone';
import { PayPartnerButton, type ReferralBalance } from './PayPartnerButton';
import { PARTNER_FIELDS, PROFILES_PATH, type ReferralPartner } from './partner';
import { StatementSection } from './StatementSection';

/**
 * One taxi driver or agency, and everything about doing business with them.
 *
 * There was no such thing before this. A partner's balance was on one tab, what they had earned on
 * another, what they had been paid on a third and their bank details on a fourth — none of the four
 * keyed to a person, and the commission history could not be narrowed to one at all. So the question
 * a driver actually asks at the bar, "how much am I owed and when was I last paid", could only be
 * answered by reading four tables and matching a name by eye across them.
 *
 * The two things you do about a partner are here as well: hand him his money, and correct how he
 * wants to be paid. Neither used to be possible from anything keyed to him.
 */

const BALANCES_PATH = '/admin/v1/referrals/balances';
const ACCRUALS_PATH = '/admin/v1/referrals/accruals';
const PAYOUT_LINES_PATH = '/admin/v1/referrals/payout-lines';

interface Accrual {
  id: string;
  at: string;
  level: string;
  baseMinor: number;
  baseCurrency: string;
  rateBp?: number | null;
  amountMinor: number;
  currency: string;
  kind: string;
}

/**
 * The ledger, and the total of it, which are two different things.
 *
 * `rows` is a page — the most recent five hundred. `totals` is every accrual that matched, worked
 * out on the server. This page used to add the rows up and print the answer as what a partner had
 * earned, so the figure was correct until his five hundred and first bill and then quietly short by
 * whatever the page had cut off. Nothing on the screen said so, and under a ledger that recomputes
 * itself every night five hundred bills is a season.
 */
interface AccrualLedger {
  rows: Accrual[];
  totals: { currency: string; amountMinor: number; count: number }[];
  truncated: boolean;
}

interface PayoutLine {
  id: string;
  amountMinor: number;
  paidMinor?: number;
  currency: string;
  status: string;
  methodUsed?: string | null;
  reference?: string | null;
  paidAt?: string | null;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text size="sm" mt={2}>
        {children}
      </Text>
    </div>
  );
}

function Missing({ children }: { children: ReactNode }) {
  return (
    <Text span size="sm" c="dimmed" fs="italic">
      {children}
    </Text>
  );
}

export function PartnerDetailPage() {
  const { profileId = '' } = useParams();
  const [editing, setEditing] = useState(false);
  /**
   * Which nights the commission history covers. EMPTY is everything, which is what this page has
   * always shown and what somebody arriving at it expects — a range that defaulted to a month would
   * hide older commission behind a control nobody had touched. It is here for the partner whose
   * history has outgrown one page, where narrowing is the only way to reach what the page cut off.
   */
  const [earnedRange, setEarnedRange] = useState<[Date | null, Date | null]>([null, null]);
  const earnedFrom = toIsoDate(earnedRange[0]);
  const earnedTo = toIsoDate(earnedRange[1]);

  const profilesQuery = useQuery({
    queryKey: listKey(PROFILES_PATH),
    queryFn: () => rawApi<ReferralPartner[]>('GET', PROFILES_PATH),
  });
  const partner = (profilesQuery.data ?? []).find((p) => p.id === profileId);

  const balancesQuery = useQuery({
    queryKey: listKey(BALANCES_PATH),
    queryFn: () => rawApi<ReferralBalance[]>('GET', BALANCES_PATH),
  });
  const owed = (balancesQuery.data ?? []).filter((b) => b.profileId === profileId);

  const accrualsQuery = useQuery({
    queryKey: listKey(ACCRUALS_PATH, { profileId, from: earnedFrom, to: earnedTo }),
    queryFn: () =>
      rawApi<AccrualLedger>(
        'GET',
        [
          `${ACCRUALS_PATH}?profileId=${encodeURIComponent(profileId)}`,
          earnedFrom ? `&from=${earnedFrom}` : '',
          earnedTo ? `&to=${earnedTo}` : '',
        ].join(''),
      ),
    enabled: Boolean(profileId),
  });

  const payoutsQuery = useQuery({
    queryKey: listKey(PAYOUT_LINES_PATH, { profileId, includePaid: 'true' }),
    queryFn: () =>
      rawApi<PayoutLine[]>('GET', `${PAYOUT_LINES_PATH}?profileId=${profileId}&includePaid=true`),
    enabled: Boolean(profileId),
  });

  const earned = accrualsQuery.data?.rows ?? [];
  // Struck on the server over every accrual that matched, never over the rows on screen. Lempiras
  // and dollars stay separate debts here as everywhere; a single total across both would be a
  // figure that is true of neither.
  const earnedTotals = accrualsQuery.data?.totals ?? [];
  const narrowed = Boolean(earnedFrom || earnedTo);
  const payouts = payoutsQuery.data ?? [];
  const lastPaid = payouts.find((p) => p.paidAt);

  const paysByTransfer = partner?.preferredPayoutType === 'transfer';

  return (
    <RecordScreen
      backTo="/referrals"
      backLabel="All partners"
      isLoading={profilesQuery.isLoading}
      /*
       * This page finds its partner inside the list it fetched, so "the request failed" and "they
       * are not in it" arrive as the same empty answer. Told apart, because announcing that a driver
       * is no longer on file — when he is, and a fetch merely timed out — is a sentence somebody
       * would act on.
       */
      loadFailed={profilesQuery.isError && !profilesQuery.data}
      loadFailedMessage="This partner's record could not be read just now. Nothing has changed. Refresh the page to try again."
      notFound={!profilesQuery.isLoading && !profilesQuery.isError && !partner}
      notFoundMessage="This partner is no longer on file."
      title={partner?.name ?? ''}
      subtitle={
        partner
          ? [
              enumLabel('referralPartyKind', partner.type),
              partner.commissionPlanId ? undefined : 'on no commission plan',
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      status={partner?.active === false ? { label: 'No longer working with you', color: 'gray' } : undefined}
      actions={
        partner ? (
          <Group gap="xs">
            {/* The money first: a driver at the bar asking for his cash is why anybody opens this
                page. One button per currency he is owed, because they are separate handovers. */}
            {owed.map((balance) => (
              <PayPartnerButton
                key={balance.currency}
                balance={balance}
                alsoOwed={owed.filter((b) => b.currency !== balance.currency)}
                size="sm"
                label={
                  owed.length > 1 ? `Pay ${formatMoney(balance.accruedMinor, balance.currency)}` : 'Pay them'
                }
              />
            ))}
            <Button variant="default" size="sm" onClick={() => setEditing(true)}>
              Change details
            </Button>
          </Group>
        ) : undefined
      }
      facts={
        partner
          ? [
              ...owed.map((b) => ({
                label: `Owed (${b.currency})`,
                value: (
                  <>
                    {formatMoney(b.accruedMinor, b.currency)}
                    {!b.payable && b.shortfallMinor ? (
                      <Text span size="xs" c="orange">
                        {' '}
                        · {formatMoney(b.shortfallMinor, b.currency)} short of their minimum
                      </Text>
                    ) : null}
                  </>
                ),
              })),
              ...(owed.length === 0 ? [{ label: 'Owed', value: null, empty: 'Nothing right now' }] : []),
              // The label follows the question that was actually asked. Narrow the nights below and
              // this is the answer for those nights — a heading still reading "in all" over a
              // narrowed figure is the same lie the capped page used to tell, differently worded.
              ...earnedTotals.map((t) => ({
                label: `${narrowed ? 'Earned over these nights' : 'Earned in all'} (${t.currency})`,
                value: formatMoney(t.amountMinor, t.currency),
              })),
              {
                label: 'Paid by',
                value: enumLabel('payoutMethod', partner.preferredPayoutType),
              },
              {
                label: 'Last paid',
                value: lastPaid?.paidAt ? formatDate(lastPaid.paidAt) : null,
                empty: 'Never',
              },
            ]
          : []
      }
      /*
       * WHAT IS OWED CAN STILL MOVE, said where the figure is rather than behind anything.
       *
       * An unpaid commission is provisional: working it out again restates it, a refunded bill
       * takes it back, and crossing a volume tier lifts the nights before it to the better rate.
       * Only paying closes it. Without this line the number reads as a promise, and the owner
       * reads it out to a driver on Tuesday who is handed different money on Friday — which is the
       * argument this screen exists to end.
       *
       * Said in the words the statement uses and no stronger. This must not claim a figure is
       * refreshed on a schedule that does not exist yet; what is true today and after the sweep
       * lands alike is that an unpaid figure is not final.
       */
      factsNote={
        partner && owed.length > 0
          ? 'What is owed is not final until it is paid — working the commission out again can move it: a refunded bill takes commission back, and a higher rate adds to it.'
          : undefined
      }
      sections={
        partner
          ? [
              {
                title: 'How to reach them, and how they are paid',
                description: 'Change any of this here — it is the only place it lives.',
                children: (
                  <Stack gap="md">
                    {/* Only for somebody actually paid by transfer. A partner paid in cash has no
                        account to check, and a warning there sends the office looking for a problem
                        that does not exist. */}
                    {paysByTransfer && !partner.bankDetailsVerified && (
                      <Alert color="orange" variant="light" title="Their bank details need checking">
                        {partner.name} is paid by transfer, and these details have not been confirmed since
                        they last changed. Check them against what {partner.name} gave you before the next
                        transfer goes out.
                      </Alert>
                    )}
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                      <Fact label="Phone">
                        {partner.phone ? (
                          <Anchor href={`tel:${dialablePhone(partner.phone)}`}>
                            {formatPhone(partner.phone)}
                          </Anchor>
                        ) : (
                          <Missing>Not recorded</Missing>
                        )}
                      </Fact>
                      <Fact label="WhatsApp">
                        {partner.whatsapp ? formatPhone(partner.whatsapp) : <Missing>Not recorded</Missing>}
                      </Fact>
                      <Fact label="Email">
                        {partner.email ? (
                          <Anchor href={`mailto:${partner.email}`}>{partner.email}</Anchor>
                        ) : (
                          <Missing>Not recorded</Missing>
                        )}
                      </Fact>
                      <Fact label="Drives for">
                        {partner.parentId ? (
                          <EntityLabel entity="referralProfile" id={partner.parentId} />
                        ) : (
                          <Missing>Works on their own</Missing>
                        )}
                      </Fact>
                      <Fact label="Commission plan">
                        {partner.commissionPlanId ? (
                          <EntityLabel entity="commissionPlan" id={partner.commissionPlanId} />
                        ) : (
                          <Missing>None — they earn nothing yet</Missing>
                        )}
                      </Fact>
                      <Fact label="ID">
                        {partner.idDocumentNumber ? (
                          `${partner.idDocumentType ? `${enumLabel('idDocumentType', partner.idDocumentType)} ` : ''}${partner.idDocumentNumber}`
                        ) : (
                          <Missing>Not recorded</Missing>
                        )}
                      </Fact>
                      {paysByTransfer && (
                        <>
                          <Fact label="Bank">{partner.bankName || <Missing>Not recorded</Missing>}</Fact>
                          <Fact label="Account">
                            {partner.accountNumber ? (
                              <>
                                {partner.accountNumber}
                                {partner.accountCurrency ? ` · ${partner.accountCurrency}` : ''}
                              </>
                            ) : (
                              <Missing>None — they cannot be paid by transfer yet</Missing>
                            )}
                          </Fact>
                          <Fact label="Name on the account">{partner.accountHolderName || partner.name}</Fact>
                        </>
                      )}
                      <Fact label="Minimum before a payout">
                        {partner.payoutThresholds?.length ? (
                          partner.payoutThresholds
                            .map(
                              (t) =>
                                `${formatMoney(t.thresholdMinor, t.currency)} ${enumLabel('payoutMethod', t.payoutType).toLowerCase()}`,
                            )
                            .join(' · ')
                        ) : (
                          <Missing>None — anything owed can be handed over</Missing>
                        )}
                      </Fact>
                      {partner.notes && <Fact label="Notes">{partner.notes}</Fact>}
                    </SimpleGrid>
                  </Stack>
                ),
              },
              {
                title: 'Give them a statement',
                description:
                  // The thing that did not exist: a driver who disputed a figure had the owner's
                  // screen and nothing to take away, so the same conversation happened next week.
                  // And until now the only way to see what he would be handed was to make the file.
                  'Every bill behind their commission over the nights you choose — on screen, and as a file you can hand over or send.',
                children: <StatementSection profileId={partner.id} partnerName={partner.name} />,
              },
              {
                title: 'What they have been paid',
                description: 'Every payout of theirs, and what was actually handed over.',
                children: payoutsQuery.isLoading ? (
                  <EmptyNote>Loading…</EmptyNote>
                ) : payouts.length === 0 ? (
                  <EmptyNote>Nothing has been handed over to {partner.name} yet.</EmptyNote>
                ) : (
                  <Table.ScrollContainer minWidth={560}>
                    <Table verticalSpacing="sm" highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Paid on</Table.Th>
                          <Table.Th>How</Table.Th>
                          <Table.Th>Reference</Table.Th>
                          <Table.Th ta="right">Amount</Table.Th>
                          <Table.Th ta="right">Where it stands</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {payouts.map((line) => (
                          <Table.Tr key={line.id}>
                            <Table.Td>
                              {line.paidAt ? (
                                formatDate(line.paidAt)
                              ) : (
                                <Text size="sm" c="dimmed">
                                  Not yet
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {line.methodUsed ? enumLabel('payoutMethod', line.methodUsed) : '—'}
                            </Table.Td>
                            <Table.Td>{line.reference || '—'}</Table.Td>
                            <Table.Td ta="right">{formatMoney(line.amountMinor, line.currency)}</Table.Td>
                            <Table.Td ta="right">
                              {line.status === 'paid' ? (
                                <Badge color="teal" variant="light">
                                  Paid
                                </Badge>
                              ) : (
                                <Text size="xs" c="orange">
                                  {formatMoney(line.amountMinor - (line.paidMinor ?? 0), line.currency)} still
                                  to hand over
                                </Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                ),
              },
              {
                title: 'What they have earned',
                description:
                  // See the note on the same sentence in `ReferralsPage.tsx`: nothing reverses a
                  // commission that has already been paid, so the screen must not say it does.
                  'Every bill tagged to them and what it earned. A bill voided before the commission has been paid drops off the next time you work commission out; one that has already been paid stays, and is settled by taking it off the next payout.',
                // Empty covers everything, which is what this list has always shown. It is here so
                // that a partner whose history has outgrown one page can still be read past it.
                actions: (
                  <DatePickerInput
                    type="range"
                    size="xs"
                    clearable
                    placeholder="All nights"
                    aria-label="Nights this covers"
                    value={earnedRange}
                    onChange={(value) => setEarnedRange(value as unknown as [Date | null, Date | null])}
                    w={230}
                    allowSingleDateInRange
                  />
                ),
                children: accrualsQuery.isLoading ? (
                  <EmptyNote>Loading…</EmptyNote>
                ) : earned.length === 0 ? (
                  <EmptyNote>
                    {narrowed
                      ? `${partner.name} earned nothing over these nights.`
                      : `Nothing yet. Commission appears here as soon as a bill is tagged to ${partner.name}.`}
                  </EmptyNote>
                ) : (
                  <Stack gap="xs">
                    {/*
                      Said out loud when the list is short of the ledger, because a table that
                      simply stops reads as the end of the history. The totals above are NOT short
                      of it — they are struck on the server over every bill — which is why this is
                      a note about the list and not a warning about a figure.
                    */}
                    {accrualsQuery.data?.truncated && (
                      <Text size="xs" c="dimmed">
                        The most recent {earned.length} of{' '}
                        {accrualsQuery.data.totals.reduce((n, t) => n + t.count, 0)} are listed here. Narrow
                        the nights to read the rest; what {partner.name} has earned is totaled over all of
                        them either way.
                      </Text>
                    )}
                    <Table.ScrollContainer minWidth={620}>
                      <Table verticalSpacing="sm" highlightOnHover striped>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>When</Table.Th>
                            <Table.Th>Earned by</Table.Th>
                            <Table.Th ta="right">Bill</Table.Th>
                            <Table.Th ta="right">Share</Table.Th>
                            <Table.Th ta="right">Commission</Table.Th>
                            <Table.Th>What happened</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {earned.map((a) => (
                            <Table.Tr key={a.id}>
                              <Table.Td>{formatDateTime(a.at)}</Table.Td>
                              <Table.Td>{enumLabel('referralLevel', a.level)}</Table.Td>
                              <Table.Td ta="right">{formatMoney(a.baseMinor, a.baseCurrency)}</Table.Td>
                              <Table.Td ta="right">
                                {typeof a.rateBp === 'number' ? formatRateBp(a.rateBp) : '—'}
                              </Table.Td>
                              <Table.Td ta="right" fw={600}>
                                {formatMoney(a.amountMinor, a.currency)}
                              </Table.Td>
                              <Table.Td>{enumLabel('referralEntryKind', a.kind)}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Stack>
                ),
              },
            ]
          : []
      }
    >
      {partner && (
        <RecordEditModal
          opened={editing}
          onClose={() => setEditing(false)}
          title={`Change ${partner.name}`}
          fields={PARTNER_FIELDS}
          record={partner as unknown as Record<string, unknown>}
          path={`${PROFILES_PATH}/${partner.id}`}
          invalidate={[PROFILES_PATH, BALANCES_PATH]}
          savedLabel={partner.name}
        />
      )}
    </RecordScreen>
  );
}
